import { DndContext, DragOverlay } from '@dnd-kit/core'
import { restrictToWindowEdges } from '@dnd-kit/modifiers'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Input, ModuleHeader, Text } from '@viapeople/components'
import type { ChoicesFieldCustomProps } from '@viapeople/components/src/components/forms/types'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { EvaluationHeaderModal } from '../../components/EvaluationHeaderModal'
import { useToast } from '../../utils/ToastContext'
import { trpc } from '../../utils/trpc'
import { BulkEditModal } from './components/BulkEditModal'
import { Canvas } from './components/Canvas'
import { ComponentPalette } from './components/ComponentPalette'
import { ComponentPaletteCard } from './components/ComponentPaletteCard'
import { FormSettingsModal } from './components/FormSettingsModal'
import { PreviewFormModal } from './components/PreviewFormModal'
import { PropertyPanel } from './components/PropertyPanel'
import {
	type ExportedForm,
	regenerateComponentIds,
	validateAndReplacePermissionTemplates,
} from './helpers/formExportImport'
import { normalizeFormForAdmin } from './helpers/formNormalization'
import getErrorMessage from './helpers/getFormsErrorMessage'
import { useFormBuilderDrag } from './hooks/useFormBuilderDrag'
import { useFormBuilderStore } from './store'
import type { FormComponent, FormEditInput, FormInputComponent } from './types'

export function FormBuilder() {
	const {
		activeId,
		activeDragData,
		sensors,
		customCollisionDetection,
		handleDragStart,
		handleDragEnd,
		handleDragCancel,
	} = useFormBuilderDrag()

	const { formComponents, loadForm } = useFormBuilderStore()
	const uploadingCount = useFormBuilderStore((state) => state.uploadingCount)
	const isUploadingAssets = uploadingCount > 0
	const queryClient = useQueryClient()
	const toast = useToast()
	const [formName, setFormName] = useState('')
	const [nameHasError, setNameHasError] = useState(false)
	const hasImportedRef = useRef(false)
	const [isBulkEditOpen, setIsBulkEditOpen] = useState(false)
	const [isPreviewOpen, setIsPreviewOpen] = useState(false)
	const [isEvaluationHeaderOpen, setIsEvaluationHeaderOpen] = useState(false)
	const [isFormSettingsOpen, setIsFormSettingsOpen] = useState(false)

	const [propertyPanelWidth, setPropertyPanelWidth] = useState(240)
	const isResizingRef = useRef(false)
	const cleanupResizeRef = useRef<(() => void) | null>(null)

	useEffect(() => {
		return () => {
			cleanupResizeRef.current?.()
		}
	}, [])

	const handleResizeMouseDown = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault()
			isResizingRef.current = true

			const startX = e.clientX
			const startWidth = propertyPanelWidth

			const onMouseMove = (e: MouseEvent) => {
				if (!isResizingRef.current) return
				const delta = startX - e.clientX
				const newWidth = Math.min(Math.max(startWidth + delta, 240), 600)
				setPropertyPanelWidth(newWidth)
			}

			const cleanup = () => {
				isResizingRef.current = false
				document.removeEventListener('mousemove', onMouseMove)
				document.removeEventListener('mouseup', cleanup)
				document.body.style.cursor = ''
				document.body.style.userSelect = ''
				cleanupResizeRef.current = null
			}

			cleanupResizeRef.current = cleanup
			document.body.style.cursor = 'col-resize'
			document.body.style.userSelect = 'none'
			document.addEventListener('mousemove', onMouseMove)
			document.addEventListener('mouseup', cleanup)
		},
		[propertyPanelWidth],
	)

	const navigate = useNavigate()
	const location = useLocation()
	const { formsId: formId, organizationId } = useParams<{
		organizationId: string
		formsId: string
	}>()

	// Check for imported form data in location state
	const importedForm = (location.state as { importedForm?: ExportedForm })?.importedForm

	const formQuery = useQuery({
		...trpc.admin.forms.getById.queryOptions({
			id: formId as string,
			organizationId: organizationId as string,
		}),
		enabled: !!formId && formId !== 'new' && !!organizationId,
	})

	const organizationQuery = useQuery({
		...trpc.admin.organizations.getById.queryOptions({ id: organizationId as string }),
		enabled: !!organizationId,
	})

	const defaultTemplateQuery = useQuery({
		...trpc.admin.permissionTemplates.search.queryOptions({
			organizationId: organizationId as string,
			search: '',
			perPage: 1,
			page: 1,
		}),
		enabled: !!organizationId,
	})

	// Query to get all permission templates for validation during import
	const allTemplatesQuery = useQuery({
		...trpc.admin.permissionTemplates.search.queryOptions({
			organizationId: organizationId as string,
			search: '',
			perPage: 1000,
			page: 1,
		}),
		enabled: !!organizationId && !!importedForm,
	})

	useEffect(() => {
		if (formQuery.data) {
			loadForm({
				name: formQuery.data.name,
				components: normalizeFormForAdmin(formQuery.data.components),
			})
			setFormName(formQuery.data.name)
		}
	}, [formQuery.data, loadForm])

	// Handle imported form data
	useEffect(() => {
		if (
			importedForm &&
			formId === 'new' &&
			!hasImportedRef.current &&
			allTemplatesQuery.data &&
			defaultTemplateQuery.data?.data?.[0]?.id
		) {
			hasImportedRef.current = true

			// Get available template IDs
			const availableTemplateIds = allTemplatesQuery.data.data.map((t) => t.id)
			const defaultTemplateId = defaultTemplateQuery.data.data[0].id

			// Regenerate all component IDs to avoid conflicts
			let componentsWithNewIds = regenerateComponentIds(importedForm.components)

			// Count how many components had invalid permission templates
			const originalTemplateIds = new Set<string>()
			const countTemplates = (components: FormInputComponent[]) => {
				components.forEach((component) => {
					originalTemplateIds.add(component.permissionTemplateId)
					if (component.children) {
						countTemplates(component.children)
					}
				})
			}
			countTemplates(componentsWithNewIds)

			// Validate and replace permission templates
			componentsWithNewIds = validateAndReplacePermissionTemplates(
				componentsWithNewIds,
				availableTemplateIds,
				defaultTemplateId,
			)

			// Check if any templates were replaced
			const invalidTemplates = Array.from(originalTemplateIds).filter((id) => !availableTemplateIds.includes(id))

			loadForm({
				name: importedForm.name,
				components: normalizeFormForAdmin(
					componentsWithNewIds.map((c) => ({
						...c,
						action: 'create',
					})),
				),
			})
			setFormName(importedForm.name)

			if (invalidTemplates.length > 0) {
				toast.showToast(
					`Form imported. ${invalidTemplates.length} permission template(s) not found and replaced with default.`,
					'info',
				)
			} else {
				toast.showToast('Form imported successfully')
			}

			// Clear the location state to prevent re-importing on refresh
			navigate(location.pathname, { replace: true, state: {} })
		}
	}, [
		importedForm,
		formId,
		loadForm,
		toast,
		navigate,
		location.pathname,
		allTemplatesQuery.data,
		defaultTemplateQuery.data,
	])

	const saveMutation = useMutation({
		...trpc.admin.forms.save.mutationOptions(),
		onSettled: () => {
			queryClient.invalidateQueries({
				queryKey: trpc.admin.forms.getById.queryKey({
					id: formId as string,
					organizationId: organizationId as string,
				}),
				exact: false,
				refetchType: 'all',
			})
		},
	})

	const handleSave = () => {
		if (!organizationId) return

		if (!defaultTemplateQuery.data?.data?.[0]?.id) {
			toast.showToast('No permission template found. Please create one first.', 'error')
			return
		}

		if (!formName) {
			toast.showToast('Form name is required', 'error')
			setNameHasError(true)
			return
		}

		const getComponentDefinition = useFormBuilderStore.getState().getComponentDefinition

		const validateComponents = (components: FormInputComponent[]): boolean => {
			for (const component of components) {
				const customProps = component.custom as Record<string, unknown>
				if (component.action === 'delete') {
					continue
				}

				// Widgets that don't expose a generic `label` custom field — their
				// schemas use widget-specific labels (e.g. HR Review Flag has
				// `widgetLabel` + `toggleLabel`) so the generic label check
				// doesn't apply.
				const optionalLabelComponents = ['image', 'divider', 'container', 'hrreviewflag']
				const componentDef = getComponentDefinition(component.name)

				if (!customProps?.label && !optionalLabelComponents.includes(component.name)) {
					toast.showToast(`Label is required for ${componentDef?.displayName || 'component'}`, 'error')
					return false
				}

				if (component.name === 'radio-group') {
					const { options } = component.custom as ChoicesFieldCustomProps

					const selectOptions =
						typeof options === 'string'
							? options
									.split(',')
									.filter((o) => o.trim())
									.map((o) => ({ value: o.trim(), label: o.trim() }))
							: options

					if (selectOptions.length === 0) {
						toast.showToast('At least one option is required for Choices component', 'error')
						return false
					}

					if (selectOptions.some((option) => !option.label || option.label.trim() === '')) {
						toast.showToast('All option labels are required for Choices component', 'error')
						return false
					}
				}

				if (component.type === 'display' && typeof customProps?.text === 'string' && customProps?.text.trim() === '') {
					toast.showToast(`Text content is required for ${componentDef?.displayName || 'component'}`, 'error')
					return false
				}
				if (component.children && component.children.length > 0) {
					if (!validateComponents(component.children)) {
						return false
					}
				}
			}
			return true
		}

		if (!validateComponents(formComponents)) {
			return
		}

		const payload = {
			organizationId,
			name: formName,
			components: formComponents.map((component) => ({
				...component,
				containerId: (component as FormComponent).containerId || null,
				label: ((component.custom as Record<string, unknown>)?.label as string) || '',
			})),
		}

		try {
			saveMutation.mutate({ ...payload, id: formId ?? null } as FormEditInput, {
				onSuccess: (data) => {
					queryClient.invalidateQueries({ queryKey: [['forms']] })
					toast.showToast('Form saved successfully')
					if (data) {
						navigate(`/organizations/${data.organizationId}/forms/${data.id}`)
					}
				},
				onError: (error) => toast.showToast(getErrorMessage(error), 'error'),
			})
		} catch (error) {
			console.error('Error saving form:', error)
			toast.showToast('Error saving form', 'error')
		}
	}

	if (!organizationId) {
		return (
			<main className='p-6'>
				<Text>Missing organization ID</Text>
			</main>
		)
	}

	if (organizationQuery.isError) {
		return (
			<main className='p-6'>
				<Text>Error loading organization</Text>
			</main>
		)
	}

	if (formId && formQuery.isError) {
		return (
			<main className='p-6'>
				<Text>Error loading form</Text>
			</main>
		)
	}

	return (
		<main className='h-dvh flex flex-col'>
			<div className='p-6 pb-1'>
				<ModuleHeader
					breadcrumb={[
						{ label: 'Organizations', to: '/organizations' },
						{ label: organizationQuery.data?.name || '', to: `/organizations/${organizationId}` },
						{
							label: 'Forms',
							to: `/organizations/${organizationId}/forms`,
						},
						{ label: 'Form Builder' },
					]}
					logo={{
						url: organizationQuery.data?.logo || '',
						name: organizationQuery.data?.name || '',
					}}
				/>
				<div className='flex justify-between items-center mt-6 mb-1.5 gap-x-6'>
					<div className='flex h-9 w-full max-w-100'>
						<Input
							className='h-9'
							placeholder='Form Name'
							value={formName}
							variant={nameHasError ? 'error' : 'default'}
							onChange={(e) => {
								setFormName(e.target.value)
								setNameHasError(false)
							}}
						/>
					</div>
					<div className='flex gap-x-2'>
						<Button variant='secondary' onClick={() => setIsPreviewOpen(true)} size='small'>
							Preview
						</Button>
						<Button
							variant='secondary'
							onClick={() => {
								navigate(`/organizations/${organizationId}/permission-templates`)
							}}
							size='small'>
							Permission templates
						</Button>
						{formId && formId !== 'new' && (
							<Button variant='secondary' onClick={() => setIsFormSettingsOpen(true)} size='small'>
								Form Settings
							</Button>
						)}
						{formId && formId !== 'new' && (
							<Button variant='secondary' onClick={() => setIsEvaluationHeaderOpen(true)} size='small'>
								Evaluation Header
							</Button>
						)}
						<Button variant='secondary' onClick={() => setIsBulkEditOpen(true)} size='small'>
							Bulk Edit
						</Button>
						<Button
							variant='primary'
							onClick={handleSave}
							size='small'
							disabled={isUploadingAssets}
							title={isUploadingAssets ? 'Waiting for uploads to finish' : undefined}>
							{isUploadingAssets ? 'Uploading...' : 'Save form'}
						</Button>
					</div>
				</div>
			</div>
			<div className='w-full flex-1 px-6 pb-4'>
				<DndContext
					sensors={sensors}
					collisionDetection={customCollisionDetection}
					onDragStart={handleDragStart}
					onDragEnd={handleDragEnd}
					onDragCancel={handleDragCancel}
					autoScroll={true}>
					<div className='flex h-full border rounded-lg border-neutral-200 shadow-md'>
						<div className='w-60 border-r border-neutral-200 bg-neutral-0 relative'>
							<ComponentPalette />
						</div>

						<div className='flex-1 overflow-y-auto'>
							<Canvas />
						</div>

						<div
							className='border-l border-neutral-200 bg-neutral-0 relative shrink-0'
							style={{ width: propertyPanelWidth }}>
							<div
								role='slider'
								tabIndex={0}
								aria-valuenow={propertyPanelWidth}
								aria-valuemin={240}
								aria-valuemax={600}
								aria-label='Resize properties panel'
								className='absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary-300 active:bg-primary-400 z-10 transition-colors'
								onMouseDown={handleResizeMouseDown}
							/>
							<PropertyPanel />
						</div>
					</div>

					<DragOverlay
						modifiers={[restrictToWindowEdges]}
						zIndex={9999}
						dropAnimation={{
							duration: 200,
							easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
						}}>
						{activeId && activeDragData ? (
							activeDragData.component ? (
								<div className='rotate-[-8deg] translate-x-1/2  translate-y-1/2'>
									<ComponentPaletteCard component={activeDragData.component} />
								</div>
							) : (
								<div className='p-4 bg-neutral-0 border-2 border-primary-500 rounded shadow-md'>
									<Text variant='small'>Dragging component</Text>
								</div>
							)
						) : null}
					</DragOverlay>
				</DndContext>
			</div>
			<BulkEditModal isOpen={isBulkEditOpen} onClose={() => setIsBulkEditOpen(false)} />
			{formId && formId !== 'new' && (
				<FormSettingsModal isOpen={isFormSettingsOpen} onClose={() => setIsFormSettingsOpen(false)} formId={formId} />
			)}
			<PreviewFormModal isOpen={isPreviewOpen} onClose={() => setIsPreviewOpen(false)} />
			{formId && formId !== 'new' && organizationId && (
				<EvaluationHeaderModal
					isOpen={isEvaluationHeaderOpen}
					onClose={() => setIsEvaluationHeaderOpen(false)}
					organizationId={organizationId}
					formId={formId}
				/>
			)}
		</main>
	)
}
